import React, { createContext, useContext, ReactNode } from 'react';
import { IStorageService } from '../services/interfaces/IStorageService';
import { INetworkService } from '../services/interfaces/INetworkService';
import { IBiometricService } from '../services/interfaces/IBiometricService';
import { IAnalyticsService } from '../services/interfaces/IAnalyticsService';
import { IConnectivityService } from '../services/interfaces/IConnectivityService';
import { IEventService } from '../services/interfaces/IEventService';
import { IRealtimeService } from '../services/interfaces/IRealtimeService';

/**
 * Service Registry Interface
 * Defines the contract for all platform-specific services.
 */
export interface ServiceRegistry {
    storage: IStorageService;
    sessionStorage: IStorageService;
    network: INetworkService;
    biometric: IBiometricService;
    analytics: IAnalyticsService;
    connectivity: IConnectivityService;
    events: IEventService;
    realtime: IRealtimeService;
}

const ServiceContext = createContext<ServiceRegistry | null>(null);

interface ServiceProviderProps {
    services: ServiceRegistry;
    children: ReactNode;
}

/**
 * Service Provider Component
 * Injects platform-specific services into the application tree.
 */
export const ServiceProvider: React.FC<ServiceProviderProps> = ({ services, children }) => {
    return (
        <ServiceContext.Provider value={services}>
            {children}
        </ServiceContext.Provider>
    );
};

/**
 * Custom hook to access the service registry.
 * Throws an error if used outside of ServiceProvider.
 */
export const useServices = (): ServiceRegistry => {
    const services = useContext(ServiceContext);
    if (!services) {
        throw new Error('useServices must be used within a ServiceProvider');
    }
    return services;
};
